import React from 'react';
import {
  Alert, Card, EmptyState, Skeleton, SkeletonAvatar, SkeletonButton, SkeletonRegion, useToast,
} from '@parsina/framework';
import { C, RADIUS } from '@parsina/framework/tokens';
import { MailOpen } from 'lucide-react';

import { STACK } from '../../constants/density';
import { useTranslation } from '../../i18n';
import { useClinicContext, refOf, BOOT, isPendingInvitation } from '../../context/ClinicContext.jsx';
import { bffMutation } from '../../lib/bffClient.js';
import MembershipCard from './MembershipCard.jsx';

/**
 * The two membership sections — «دعوت‌های شما» and «درخواست‌های شما».
 *
 * Shared by «شروع» (§A4, outside the shell, for someone with no clinic to show)
 * and «دعوت‌ها» (§5.5, inside the shell, for someone who already has one). One
 * component because they are one thing seen from two places, and letting them
 * drift would put the same invitation in front of the same person twice,
 * worded differently.
 */

const ACCEPT = `mutation AcceptClinicInvitation($clinicId: ID!) {
  acceptClinicInvitation(clinicId: $clinicId) { clinicId slug enterable }
}`;
const REJECT = `mutation RejectClinicInvitation($clinicId: ID!) { rejectClinicInvitation(clinicId: $clinicId) }`;
const CANCEL = `mutation CancelJoinRequest($clinicId: ID!) { cancelJoinRequest(clinicId: $clinicId) }`;

/**
 * One `MembershipCard` in placeholder — a 16px-padded `Card` holding a 38px
 * monogram, the clinic name at 14.5px over a 12px meta line, the proposed-role
 * chips, and the accept/reject pair. Same box, so the decision the reader is
 * about to make does not move under the cursor when the memberships land.
 */
function MembershipCardSkeleton() {
  return (
    <Card aria-hidden style={{ padding: 16, marginBottom: STACK.regular }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <SkeletonAvatar size={38} />
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <Skeleton width="52%" height={14.5} />
          <Skeleton width="34%" height={12} />
          <div style={{ display: 'flex', gap: 6, marginTop: 2 }}>
            <Skeleton width={64} height={22} radius={RADIUS.pill} />
            <Skeleton width={82} height={22} radius={RADIUS.pill} />
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <SkeletonButton width={104} />
        <SkeletonButton width={88} />
      </div>
    </Card>
  );
}

function Heading({ children, id }) {
  return (
    <h2
      id={id}
      style={{
        margin: `0 0 ${STACK.tight}px`, fontSize: '0.8125rem', fontWeight: 800, color: C.textPrimary,
      }}
    >
      {children}
    </h2>
  );
}

export default function MembershipSections({ onEntered, disabled }) {
  const { t } = useTranslation();
  const toast = useToast();
  const { memberships, state, refreshGrants, reload, switchClinic } = useClinicContext();
  const [busy, setBusy] = React.useState('');
  const [failed, setFailed] = React.useState('');
  const alertRef = React.useRef(null);

  const invited = memberships.filter(isPendingInvitation);
  const requested = memberships.filter((m) => m.status === 'REQUESTED');

  React.useEffect(() => {
    if (failed) alertRef.current?.focus();
  }, [failed]);

  const accept = async (m) => {
    setBusy(m.clinicId); setFailed('');
    try {
      await bffMutation(ACCEPT, { clinicId: m.clinicId }, 'AcceptClinicInvitation');
      // The token is holding grants issued BEFORE this acceptance — the composite
      // role was written to Keycloak a moment ago and a token does not
      // retroactively gain role mappings. Without this refresh the very next
      // request is FORBIDDEN and accepting looks like it failed (§A8).
      const fresh = await refreshGrants();
      toast?.success?.(t('clinic.invitations.accepted', { name: m.clinicName }));
      const now = (fresh || []).find((x) => x.clinicId === m.clinicId);
      if (now?.enterable) {
        onEntered?.(now);
        switchClinic(refOf(now));
      }
    } catch (err) {
      // E17 — withdrawn between render and press. Say precisely that, rather than
      // a generic failure at the one moment a new colleague is forming an
      // impression of the product.
      setFailed(err?.extensions?.code === 'INVITATION_INVALID'
        ? t('clinic.invitations.noLongerValid')
        : (err.message || t('errors.generic')));
    } finally {
      setBusy('');
    }
  };

  const reject = async (m) => {
    setBusy(m.clinicId);
    try {
      await bffMutation(REJECT, { clinicId: m.clinicId }, 'RejectClinicInvitation');
      await reload({ silent: true });
      toast?.info?.(t('clinic.invitations.rejected'));
    } catch (err) {
      setFailed(err.message || t('errors.generic'));
    } finally { setBusy(''); }
  };

  const cancel = async (m) => {
    setBusy(m.clinicId);
    try {
      await bffMutation(CANCEL, { clinicId: m.clinicId }, 'CancelJoinRequest');
      await reload({ silent: true });
    } catch (err) {
      setFailed(err.message || t('errors.generic'));
    } finally { setBusy(''); }
  };

  // Loading first, and separately. `memberships` is empty while the boot query
  // is still in flight, so testing emptiness before the state told the reader
  // «دعوتی ندارید» about a list that had not been fetched — the three-states
  // rule (DESIGN.md §"Loading & skeleton"): loading is not empty.
  if (state === BOOT.LOADING) {
    return (
      <SkeletonRegion
        // `as="section"`: what lands here is a `<section aria-labelledby>` per
        // membership group, so the placeholder sits in the same document
        // outline rather than collapsing the page to a run of divs.
        as="section"
        label={t('common.state.loading')}
        data-testid="membership-skeleton"
      >
        <Skeleton width={96} height={13} style={{ marginBottom: STACK.tight }} />
        {[0, 1].map((i) => (
          <MembershipCardSkeleton key={i} />
        ))}
      </SkeletonRegion>
    );
  }

  if (!invited.length && !requested.length) {
    return (
      <EmptyState
        icon={<MailOpen size={44} strokeWidth={1.5} aria-hidden />}
        title={t('clinic.invitations.emptyTitle')}
        description={t('clinic.invitations.emptyBody')}
      />
    );
  }

  return (
    <div>
      {failed ? (
        <div ref={alertRef} tabIndex={-1} style={{ outline: 'none', marginBottom: STACK.regular }}>
          <Alert variant="error" role="alert" data-testid="membership-error">{failed}</Alert>
        </div>
      ) : null}

      {invited.length ? (
        <section aria-labelledby="inv-h" style={{ marginBottom: 20 }}>
          <Heading id="inv-h">{t('clinic.invitations.invitedSection')}</Heading>
          {invited.map((m) => (
            <MembershipCard
              key={m.clinicId}
              membership={m}
              variant="invited"
              busy={busy === m.clinicId}
              disabled={disabled || (!!busy && busy !== m.clinicId)}
              onAccept={accept}
              onReject={reject}
            />
          ))}
        </section>
      ) : null}

      {requested.length ? (
        <section aria-labelledby="req-h">
          <Heading id="req-h">{t('clinic.invitations.requestedSection')}</Heading>
          {requested.map((m) => (
            <MembershipCard
              key={m.clinicId}
              membership={m}
              variant="requested"
              busy={busy === m.clinicId}
              disabled={disabled || (!!busy && busy !== m.clinicId)}
              onCancel={cancel}
            />
          ))}
        </section>
      ) : null}
    </div>
  );
}
